import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { RatingModal } from '../components/RatingModal';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { vehicleAPI, rentalAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Key,
  Car,
  MapPin,
  Calendar as CalendarIcon,
  Star,
  DollarSign,
  Clock,
  Filter,
  Search,
  ArrowRight,
  X,
  CheckCircle2
} from 'lucide-react';

const VehicleCard = ({ vehicle, onSelect }) => (
  <Card 
    className="card-elevated overflow-hidden group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
    onClick={() => onSelect(vehicle)}
    data-testid={`vehicle-card-${vehicle.id}`}
  >
    <div className="h-40 bg-gradient-to-br from-[#00BFA6]/10 to-[#00887A]/10 flex items-center justify-center">
      <Car className="w-20 h-20 text-[#00BFA6]/40" />
    </div>
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h3 className="font-semibold text-slate-800">{vehicle.make} {vehicle.model}</h3>
          <p className="text-slate-500 text-sm">{vehicle.year}</p>
        </div>
        <div className="flex items-center gap-1 text-[#FFD166]">
          <Star className="w-4 h-4 fill-current" />
          <span className="text-sm font-medium">{vehicle.rating || '4.5'}</span>
        </div>
      </div>
      
      <div className="flex flex-wrap gap-2 mb-3">
        {vehicle.features?.slice(0, 3).map((feature, idx) => (
          <span key={idx} className="px-2 py-1 bg-slate-100 rounded-md text-xs text-slate-600">
            {feature}
          </span>
        ))}
      </div>
      
      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
        <div>
          <p className="text-slate-500 text-xs">Desde</p>
          <p className="text-xl font-bold text-slate-800">${vehicle.daily_rate}<span className="text-sm font-normal text-slate-500">/día</span></p>
        </div>
        <Button className="btn-primary h-10 text-sm" data-testid="rent-vehicle-btn">
          Rentar
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

const RentalStatusBadge = ({ status }) => {
  const statusConfig = {
    reserved: { color: 'bg-blue-100 text-blue-600', label: 'Reservado' },
    active: { color: 'bg-green-100 text-green-600', label: 'Activo' },
    completed: { color: 'bg-teal-100 text-teal-600', label: 'Completado' },
    cancelled: { color: 'bg-red-100 text-red-600', label: 'Cancelado' },
  };
  
  const config = statusConfig[status] || statusConfig.reserved;
  
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
      {config.label}
    </span>
  );
};

export default function RentalPage() {
  const { refreshUser } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  
  // Rating modal state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [completedRental, setCompletedRental] = useState(null);
  
  const [bookingData, setBookingData] = useState({
    startDate: null,
    endDate: null,
    rentalType: 'daily',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [vehiclesRes, rentalsRes] = await Promise.all([
        vehicleAPI.getAll(),
        rentalAPI.getAll()
      ]);
      setVehicles(vehiclesRes.data);
      setRentals(rentalsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredVehicles = vehicles.filter(v => {
    const matchesSearch = v.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          v.model.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || v.vehicle_type === filterType;
    return matchesSearch && matchesType;
  });

  const handleSelectVehicle = (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowBooking(true);
  };

  const calculateTotalPrice = () => {
    if (!selectedVehicle || !bookingData.startDate || !bookingData.endDate) return 0;
    const days = Math.ceil((bookingData.endDate - bookingData.startDate) / (1000 * 60 * 60 * 24)) || 1;
    return (days * selectedVehicle.daily_rate).toFixed(2);
  };

  const handleBooking = async () => {
    if (!bookingData.startDate || !bookingData.endDate) {
      toast.error('Selecciona las fechas de la reserva');
      return;
    }

    setLoading(true);
    try {
      await rentalAPI.create({
        vehicle_id: selectedVehicle.id,
        start_date: bookingData.startDate.toISOString(),
        end_date: bookingData.endDate.toISOString(),
        rental_type: bookingData.rentalType,
        payment_method: 'wallet',
      });
      
      toast.success('¡Reserva confirmada!');
      setShowBooking(false);
      setSelectedVehicle(null);
      refreshUser();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al realizar la reserva');
    } finally {
      setLoading(false);
    }
  };

  // Handle complete rental (for demo)
  const handleCompleteRental = async (rental) => {
    try {
      await rentalAPI.updateStatus(rental.id, 'completed');
      setCompletedRental(rental);
      setShowRatingModal(true);
      toast.success('¡Renta completada!');
      loadData();
    } catch (error) {
      toast.error('Error al completar la renta');
    }
  };

  const handleRatingSuccess = () => {
    setShowRatingModal(false);
    setCompletedRental(null);
  };

  const handleRatingSkip = () => {
    setShowRatingModal(false);
  };

  return (
    <Layout>
      <div className="p-4 lg:p-8 space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 font-['Outfit']">Rentar Vehículo</h1>
            <p className="text-slate-500 mt-1">Encuentra el vehículo perfecto para ti</p>
          </div>
          
          {/* Search and Filters */}
          <div className="flex gap-3">
            <div className="relative flex-1 lg:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Buscar..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 bg-white border-slate-200 text-slate-800 rounded-xl"
                data-testid="search-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40 h-12 bg-white border-slate-200 text-slate-800 rounded-xl" data-testid="filter-select">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filtrar" />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all" className="text-slate-800 hover:bg-slate-50">Todos</SelectItem>
                <SelectItem value="economy" className="text-slate-800 hover:bg-slate-50">Económico</SelectItem>
                <SelectItem value="comfort" className="text-slate-800 hover:bg-slate-50">Comfort</SelectItem>
                <SelectItem value="premium" className="text-slate-800 hover:bg-slate-50">Premium</SelectItem>
                <SelectItem value="xl" className="text-slate-800 hover:bg-slate-50">XL</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Vehicle Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-80 animate-pulse" />
            ))}
          </div>
        ) : filteredVehicles.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredVehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} onSelect={handleSelectVehicle} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No hay vehículos disponibles</p>
            <p className="text-slate-400 text-sm mt-1">Intenta con otros filtros</p>
          </div>
        )}

        {/* My Rentals */}
        {rentals.length > 0 && (
          <div className="pt-8">
            <h2 className="text-xl font-bold text-slate-800 mb-4 font-['Outfit']">Mis Reservas</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rentals.map((rental) => (
                <Card key={rental.id} className="card-elevated p-4" data-testid={`rental-${rental.id}`}>
                  <div className="flex items-center justify-between mb-3">
                    <RentalStatusBadge status={rental.status} />
                    <span className="text-slate-800 font-bold">${rental.total_price}</span>
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-500">
                      <CalendarIcon className="w-4 h-4" />
                      <span>{format(new Date(rental.start_date), 'PP', { locale: es })}</span>
                      <span>-</span>
                      <span>{format(new Date(rental.end_date), 'PP', { locale: es })}</span>
                    </div>
                  </div>
                  {/* Complete button for active rentals */}
                  {rental.status === 'active' && (
                    <Button 
                      onClick={() => handleCompleteRental(rental)}
                      className="w-full mt-4 h-10 btn-primary"
                      data-testid="complete-rental-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Completar Renta (Demo)
                    </Button>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Booking Modal */}
        {showBooking && selectedVehicle && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <Card className="card-elevated w-full max-w-md animate-slide-up">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-800 font-['Outfit']">Confirmar Reserva</h3>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowBooking(false)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                {/* Vehicle Info */}
                <div className="bg-slate-50 p-4 rounded-xl mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl bg-[#00BFA6]/10 flex items-center justify-center">
                      <Car className="w-8 h-8 text-[#00BFA6]" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800">{selectedVehicle.make} {selectedVehicle.model}</h4>
                      <p className="text-slate-500 text-sm">{selectedVehicle.year} • ${selectedVehicle.daily_rate}/día</p>
                    </div>
                  </div>
                </div>

                {/* Date Selection */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-700">Fecha inicio</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full h-12 mt-2 justify-start border-slate-200 text-slate-700 rounded-xl"
                            data-testid="start-date-btn"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {bookingData.startDate ? format(bookingData.startDate, 'PP', { locale: es }) : 'Seleccionar'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white border-slate-200">
                          <Calendar
                            mode="single"
                            selected={bookingData.startDate}
                            onSelect={(date) => setBookingData(prev => ({ ...prev, startDate: date }))}
                            disabled={(date) => date < new Date()}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    <div>
                      <Label className="text-slate-700">Fecha fin</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full h-12 mt-2 justify-start border-slate-200 text-slate-700 rounded-xl"
                            data-testid="end-date-btn"
                          >
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {bookingData.endDate ? format(bookingData.endDate, 'PP', { locale: es }) : 'Seleccionar'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-white border-slate-200">
                          <Calendar
                            mode="single"
                            selected={bookingData.endDate}
                            onSelect={(date) => setBookingData(prev => ({ ...prev, endDate: date }))}
                            disabled={(date) => date < (bookingData.startDate || new Date())}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </div>

                {/* Total */}
                <div className="bg-slate-50 p-4 rounded-xl mb-6">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Total a pagar</span>
                    <span className="text-2xl font-bold text-slate-800">${calculateTotalPrice()}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 h-12 border-slate-200 text-slate-600 rounded-xl"
                    onClick={() => setShowBooking(false)}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleBooking}
                    disabled={loading || !bookingData.startDate || !bookingData.endDate}
                    className="flex-1 h-12 btn-primary"
                    data-testid="confirm-booking-btn"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        Confirmar
                        <CheckCircle2 className="w-5 h-5 ml-2" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Rating Modal */}
      <RatingModal
        isOpen={showRatingModal}
        onClose={() => setShowRatingModal(false)}
        serviceType="rental"
        serviceId={completedRental?.id}
        ratedUserId={completedRental?.owner_id}
        ratedUserName={completedRental?.owner_name}
        canSkip={true}
        onSkip={handleRatingSkip}
        onSuccess={handleRatingSuccess}
      />
    </Layout>
  );
}
